import React, { useCallback, useContext, useState } from 'react';
import { Div, Heading, Button } from '@kizi/core-components';
import { IconV2 } from '@kizi/shared-components';
import { ProductComparison, ProductComparisonProps } from './ProductComparison';
import { getComparatorHandler } from '../../utils/comparison/helpers';
import {
  useWizardService,
  usePage,
  useField,
  useFields,
  ComparatorContext,
} from '@kizi/comparator-core';
import {
  Mobile,
  Landscape,
  LargeDesktop,
  TableAndDektop,
} from '../../utils/breakpoint';
import { SearchBar } from './SearchBar';

export type ProductComparisonLabel = {
  label: string;
  tooltip?: string;
  children?: any[];
  from?: string;
};

export interface ProductComparisonContainerProps
  extends Omit<ProductComparisonProps, 'labels' | 'filterOptionsHandler'> {
  header?: string;
  onClick?: () => void;
  addMore?: boolean;
  labels: ProductComparisonLabel[];
  withSearchBar?: boolean;
  searchBarPlaceholder?: string;
  dynamicLabelsFn?: (
    products: any[],
    priceMode: string,
  ) => ProductComparisonLabel[];
  priceMode: string;
  additionalData?: any;
}

export const ProductComparisonContainer: React.FC<
  ProductComparisonContainerProps
> = ({
  header,
  products,
  onClick,
  addMore = false,
  handler,
  labels,
  showMoreButtonLabel,
  withSearchBar = false,
  searchBarPlaceholder,
  dynamicLabelsFn,
  priceMode,
  additionalData,
  ...restProps
}) => {
  const smallScreenProductsList =
    products.length === 3 ? products.slice(0, -1) : products;
  const [filter, setFilter] = useState('');
  const [options, setOptions] = useState<any[]>([]);
  let _labels = labels;

  if (dynamicLabelsFn) {
    _labels = dynamicLabelsFn(products, priceMode);
  }

  return (
    <Div>
      {withSearchBar && (
        <SearchBar
          setFilter={setFilter}
          filter={filter}
          options={options}
          placeholder={searchBarPlaceholder}
        />
      )}

      <Div variant="outerComparisonContainer">
        {header && (
          <Heading
            fontFamily="body"
            variant="bodyImportant"
            as="h3"
            color="main.2"
            mb={0}
            mt={5}
            fontSize={[3, null, 5]}
          >
            {header}
          </Heading>
        )}
        <Div variant="innerComparisonContainer">
          <>
            <LargeDesktop>
              <ProductComparison
                {...restProps}
                products={products}
                handler={handler}
                labels={_labels}
                showMoreButtonLabel={showMoreButtonLabel}
                filter={filter}
                filterOptionsHandler={setOptions}
                flex={3}
                priceMode={priceMode}
                options={options}
                additionalData={additionalData}
              />
              {products.length < 3 && (
                <Div
                  py={6}
                  display="flex"
                  flex={1}
                  maxWidth="275px"
                  minWidth="150px"
                >
                  {addMore ? (
                    <Button onClick={onClick} variant="addMore">
                      <IconV2
                        name="plus"
                        fill="main.1"
                        width={32}
                        height={32}
                      />
                    </Button>
                  ) : null}
                </Div>
              )}
            </LargeDesktop>
            <TableAndDektop>
              <ProductComparison
                {...restProps}
                products={smallScreenProductsList}
                handler={handler}
                labels={_labels}
                showMoreButtonLabel={showMoreButtonLabel}
                filter={filter}
                filterOptionsHandler={setOptions}
                flex={3}
                priceMode={priceMode}
                options={options}
                additionalData={additionalData}
              />
            </TableAndDektop>
            <Mobile>
              <Landscape>
                <ProductComparison
                  {...restProps}
                  products={smallScreenProductsList}
                  handler={handler}
                  labels={_labels}
                  showMoreButtonLabel={showMoreButtonLabel}
                  filter={filter}
                  filterOptionsHandler={setOptions}
                  flex={3}
                  priceMode={priceMode}
                  options={options}
                  additionalData={additionalData}
                />
              </Landscape>
            </Mobile>
          </>
        </Div>
      </Div>
    </Div>
  );
};

export interface ProductComparisonContainerWrapperProps
  extends Omit<
    ProductComparisonContainerProps,
    'handler' | 'products' | 'onClick' | 'addMore' | 'priceMode'
  > {
  comparator: string;
  section: string;
  goToIndex?: string;
  priceModeField: string;
  additionalFields: string[];
  dependsOn?: {
    dependenceProperty: string;
    dependenceValue: string;
  };
}

const SECTION_PRODUCT_TYPE_MAP: Record<string, string> = {
  televisionPackages: 'television',
  callPackages: 'call',
  internetPackages: 'internet',
};

const shouldShowSection = (
  section: string,
  productTypes: string[] = [],
  name?: string,
): boolean => {
  if (name !== 'triple-play' && name !== 'os-triple-play') {
    return true;
  }

  if (!productTypes || productTypes.length === 0) {
    return true;
  }

  const mappedProductType = SECTION_PRODUCT_TYPE_MAP[section];

  if (!mappedProductType) {
    return true;
  }

  return productTypes.includes(mappedProductType);
};

export const ProductComparisonContainerWrapper: React.FC<
  ProductComparisonContainerWrapperProps
> = ({
  comparator,
  section,
  goToIndex,
  priceModeField,
  additionalFields = [],
  dependsOn,
  ...restProps
}) => {
  const { state } = usePage();
  const handler = getComparatorHandler(comparator, section);
  const { name } = useContext(ComparatorContext);
  const wizard = useWizardService();
  const filtersField = useField('overview.filters');
  const filters = JSON.parse((filtersField?.value as string) || '{}');
  const productTypes = filters?.productTypes as string[] | undefined;

  let priceMode = useField(priceModeField)?.value;
  const _additionalFields = useFields(additionalFields);

  const parseAdditionalData = () => {
    const result = {};
    _additionalFields.forEach((field) => {
      result[field?.name as string] = field?.value;
      if (field?.name === 'filters') {
        const filters = JSON.parse(field.value as string);
        priceMode = filters?.showPriceMode?.[0];
      }
    });

    return result;
  };

  const additionalData = parseAdditionalData();

  const onClick = useCallback(() => {
    if (goToIndex !== undefined) {
      wizard?.send({ type: 'GOTO', index: goToIndex });
    }
  }, [wizard, goToIndex]);

  if (!wizard) return null;

  if (
    dependsOn &&
    !filters[dependsOn.dependenceProperty]?.includes(dependsOn.dependenceValue)
  ) {
    return null;
  }

  if (!shouldShowSection(section, productTypes, name)) {
    return null;
  }

  return (
    <ProductComparisonContainer
      products={(state?.comparisonProducts as any[]) ?? []}
      priceMode={(priceMode as string) || 'month'}
      onClick={onClick}
      addMore={goToIndex !== undefined}
      handler={handler}
      {...restProps}
      additionalData={additionalData}
    />
  );
};
